
import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Partnership } from "@/Entities/Partnership";
import OSCReportService from "@/Entities/OSCReportService";
import { Card, CardContent, CardHeader, CardTitle } from "@/Components/ui/card";
import { Button } from "@/Components/ui/button";
import { Badge } from "@/Components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/Components/ui/select";
import { OSCCombobox } from "@/Components/ui/osc-combobox";
import { Building2, FileText, DollarSign, Calendar, AlertTriangle, CheckCircle, ChevronLeft, ChevronRight, Users, BarChart3, ArrowUp, FileDown, X, Search, Filter as FilterIcon } from "lucide-react";
import { format, parseISO, isValid } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";

// Helper para selecionar o nome OSC mais "limpo" para um CNPJ
const countSpecialChars = (str) => {
    if (!str) return Infinity;
    const matches = str.match(/[^a-zA-Z0-9\s]/g);
    return matches ? matches.length : 0;
};

const findBestOSCName = (names) => {
    if (!names || names.length === 0) return '';
    return names.reduce((best, current) => {
        return countSpecialChars(current) < countSpecialChars(best) ? current : best;
    }, names[0]);
};

const createCnpjToOscMap = (partnerships) => {
    if (!Array.isArray(partnerships)) return {};
    const cnpjGroups = {};
    partnerships.forEach(p => {
        if (p.cnpj && p.osc) {
            if (!cnpjGroups[p.cnpj]) cnpjGroups[p.cnpj] = [];
            cnpjGroups[p.cnpj].push(p.osc);
        }
    });

    const cnpjToOscMap = {};
    for (const cnpj in cnpjGroups) {
        const uniqueNames = [...new Set(cnpjGroups[cnpj])];
        cnpjToOscMap[cnpj] = findBestOSCName(uniqueNames);
    }
    return cnpjToOscMap;
};


const getStatusLabel = (status) => {
  switch (status) {
    case 'no_prazo':
      return 'No Prazo';
    case 'quitado':
      return 'Quitado';
    case 'em_debito':
      return 'Em Débito';
    case 'verificar':
      return 'Verificar';
    default:
      return 'Indefinido';
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case 'no_prazo':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'quitado':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'em_debito':
      return 'bg-red-100 text-red-800 border-red-200';
    case 'verificar':
      return 'bg-amber-100 text-amber-800 border-amber-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

export default function OSCReport() {
  const [partnerships, setPartnerships] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedOSC, setSelectedOSC] = useState('Todas as OSCs');
  const [selectedYear, setSelectedYear] = useState('Todos os Anos');
  const [oscList, setOscList] = useState([]);
  const [yearList, setYearList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(7);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [pdfProgress, setPdfProgress] = useState('');
  const [notification, setNotification] = useState(null);
  const [generatingDeclarationCNPJ, setGeneratingDeclarationCNPJ] = useState(null);
  const [infoModal, setInfoModal] = useState({ isOpen: false, title: '', message: '', oscName: '' });
  useBodyScrollLock(infoModal.isOpen);

  // Novos estados para seleção múltipla e pesquisa
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOSCs, setSelectedOSCs] = useState([]);
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const [showOnlySelected, setShowOnlySelected] = useState(false);
  
  // Ref para controlar cancelamento de requisições
  const abortControllerRef = useRef(null);

  // Função para converter português → valor técnico
  const getDbValueFromOSC = (oscLabel) => {
    if (oscLabel === 'Todas as OSCs') return 'all';
    return oscLabel; // OSCs específicas mantêm o valor
  };

  useEffect(() => {
    loadPartnerships();
  }, []);

  // Cleanup para cancelar geração de PDF ao sair da página
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (isGeneratingPDF && abortControllerRef.current && !abortControllerRef.current.signal.aborted) {
        // Mostrar aviso ao usuário
        const message = 'A geração do PDF será cancelada se você sair da página. Deseja continuar?';
        event.preventDefault();
        event.returnValue = message;
        return message;
      }
    };

    const handleUnload = () => {
      if (isGeneratingPDF && abortControllerRef.current && !abortControllerRef.current.signal.aborted) {
        abortControllerRef.current.abort();
      }
    };

    // Adicionar listeners apenas quando necessário
    if (isGeneratingPDF) {
      window.addEventListener('beforeunload', handleBeforeUnload);
      window.addEventListener('unload', handleUnload);
    }

    // Cleanup
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('unload', handleUnload);
    };
  }, [isGeneratingPDF]);

  const loadPartnerships = async () => {
    setIsLoading(true);
    try {
      // Buscar todas as parcerias para relatório OSC
      const response = await Partnership.listAll();
      const data = Array.isArray(response) ? response : (response.data || []);
      
      const cnpjToOscMap = createCnpjToOscMap(data);

      const partnershipsWithCanonicalName = data.map(p => ({
        ...p,
        osc_canonical: p.cnpj ? cnpjToOscMap[p.cnpj] || p.osc : p.osc
      }));
      
      setPartnerships(partnershipsWithCanonicalName);
      
      // Extrair anos únicos das parcerias
      const uniqueYears = [...new Set(partnershipsWithCanonicalName
        .map(p => p.ano)
        .filter(ano => ano && !isNaN(ano))
      )].sort((a, b) => b - a); // Ordenar do mais recente para o mais antigo
      
      setYearList(uniqueYears);
      
      // Atualizar lista de OSCs baseada no filtro atual
      updateOSCList(partnershipsWithCanonicalName, selectedYear);

    } catch (error) {
      console.error('Erro ao carregar parcerias:', error);
      setPartnerships([]);
    }
    setIsLoading(false);
  };

  // Função para atualizar lista de OSCs baseada no ano selecionado
  const updateOSCList = (partnershipsData, year) => {
    let filteredPartnerships = partnershipsData;
    
    // Filtrar por ano se não for "Todos os Anos"
    if (year !== 'Todos os Anos') {
      filteredPartnerships = partnershipsData.filter(p => p.ano === parseInt(year));
    }
    
    // Extrair OSCs únicas do conjunto filtrado
    const uniqueOSCs = [...new Set(filteredPartnerships
      .map(p => p.osc_canonical)
      .filter(Boolean)
    )].sort();
    
    setOscList(uniqueOSCs);
    
    // Se a OSC selecionada não existe no ano filtrado, resetar para "Todas as OSCs"
    if (selectedOSC !== 'Todas as OSCs' && !uniqueOSCs.includes(selectedOSC)) {
      setSelectedOSC('Todas as OSCs');
    }
  };

  // Função para obter dados filtrados por OSC e Ano
  const getFilteredData = () => {
    let filteredData = partnerships;
    
    // Filtrar por ano primeiro
    if (selectedYear !== 'Todos os Anos') {
      filteredData = filteredData.filter(p => p.ano === parseInt(selectedYear));
    }
    
    // Filtrar por OSC(s)
    if (isMultiSelectMode && selectedOSCs.length > 0) {
      // Modo seleção múltipla: filtrar por OSCs selecionadas
      filteredData = filteredData.filter(p => selectedOSCs.includes(p.osc_canonical));
    } else if (!isMultiSelectMode) {
      // Modo seleção única: usar selectedOSC
      const oscDbValue = getDbValueFromOSC(selectedOSC);
      if (oscDbValue !== 'all') {
        filteredData = filteredData.filter(p => p.osc_canonical === selectedOSC);
      }
    }
    
    return filteredData;
  };

  // Função para filtrar lista de OSCs pela pesquisa
  const getFilteredOSCList = () => {
    let list = oscList;
    
    // Filtrar por termo de pesquisa
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      list = list.filter(osc => osc.toLowerCase().includes(searchLower));
    }
    
    // Filtrar apenas selecionadas se modo ativo
    if (showOnlySelected) {
      list = list.filter(osc => selectedOSCs.includes(osc));
    }
    
    // Ordenar: selecionadas primeiro
    return list.sort((a, b) => {
      const aSelected = selectedOSCs.includes(a);
      const bSelected = selectedOSCs.includes(b);
      
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return 0;
    });
  };

  // Função para alternar seleção de uma OSC
  const toggleOSCSelection = (osc) => {
    setSelectedOSCs(prev => {
      if (prev.includes(osc)) {
        return prev.filter(item => item !== osc);
      } else {
        return [...prev, osc];
      }
    });
  };

  // Função para selecionar todas as OSCs filtradas
  const selectAllFilteredOSCs = () => {
    const filtered = getFilteredOSCList();
    setSelectedOSCs(filtered);
  };

  // Função para limpar seleção
  const clearOSCSelection = () => {
    setSelectedOSCs([]);
  };

  // Função para alternar modo de seleção
  const toggleMultiSelectMode = () => {
    const newMode = !isMultiSelectMode;
    setIsMultiSelectMode(newMode);
    
    if (newMode) {
      // Ao ativar modo múltiplo, limpar seleção única
      setSelectedOSC('Todas as OSCs');
      setSelectedOSCs([]);
    } else {
      // Ao desativar modo múltiplo, limpar seleção múltipla
      setSelectedOSCs([]);
    }
  };

  // Effect para atualizar OSCs quando o ano mudar
  useEffect(() => {
    if (partnerships.length > 0) {
      updateOSCList(partnerships, selectedYear);
    }
  }, [selectedYear, partnerships]);

  // Effect para resetar página quando filtros mudarem
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedOSC, selectedYear]);

  const getOSCStats = () => {
    const stats = {};
    const dataToProcess = getFilteredData();

    dataToProcess.forEach(p => {
      const osc = p.osc_canonical || 'Não informado';
      const numericValue = parseFloat(p.valor);

      if (!stats[osc]) {
        stats[osc] = {
          name: osc,
          // Pega o CNPJ do primeiro registro encontrado para este grupo
          cnpj: p.cnpj || 'Não informado',
          total: 0,
          valor_total: 0,
          no_prazo: 0,
          quitado: 0,
          em_debito: 0,
          verificar: 0,
          partnerships: []
        };
      }
      stats[osc].total += 1;
      stats[osc].valor_total += Number.isFinite(numericValue) ? numericValue : 0;
      stats[osc][p.status || 'verificar'] += 1;
      stats[osc].partnerships.push(p);
    });

    return Object.values(stats).sort((a, b) => b.valor_total - a.valor_total);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = typeof dateString === 'string' ? parseISO(dateString) : new Date(dateString);
      return isValid(date) ? format(date, 'dd/MM/yyyy', { locale: ptBR }) : 'N/A';
    } catch {
      return 'N/A';
    }
  };


  const generateDeclarationPDF = async (oscData) => {
    if (generatingDeclarationCNPJ === oscData.cnpj) return;
    
    setGeneratingDeclarationCNPJ(oscData.cnpj);
    
    try {
      // Passar o ano selecionado para respeitar o filtro da tela
      await OSCReportService.generateDeclarationPDF(oscData.cnpj, selectedYear);
      setGeneratingDeclarationCNPJ(null);
      showNotification('Declaração gerada com sucesso!', 'success', 3000);
    } catch (error) {
      setGeneratingDeclarationCNPJ(null);
      
      // Verificar se é erro de falta de pendências (não é um erro do sistema)
      if (error.message.includes('não possui parcerias com status')) {
        // Mostrar modal informativo ao invés de notificação de erro
        const yearInfo = selectedYear !== 'Todos os Anos' ? ` no ano ${selectedYear}` : '';
        setInfoModal({
          isOpen: true,
          title: 'Declaração não disponível',
          message: `Esta OSC não possui parcerias com status "Em Débito"${yearInfo}.`,
          oscName: oscData.name
        });
      } else {
        // Outros erros (reais) mostram notificação de erro
        showNotification(`Erro ao gerar declaração: ${error.message}`, 'error', 5000);
      }
    }
  };


  const oscStats = getOSCStats();

  // Funções de paginação
  const totalPages = Math.ceil(oscStats.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentOSCs = oscStats.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const goToPrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Calcular estatísticas gerais
  const totalOSCs = oscStats.length;
  const totalPartnerships = oscStats.reduce((sum, osc) => sum + osc.total, 0);
  const totalValue = oscStats.reduce((sum, osc) => sum + osc.valor_total, 0);

  // Função para voltar ao topo
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Função para mostrar notificação
  const showNotification = (message, type = 'info', duration = 5000) => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), duration);
  };

  // Função para cancelar geração de PDF
  const cancelPDFGeneration = () => {
    if (abortControllerRef.current && !abortControllerRef.current.signal.aborted) {
      abortControllerRef.current.abort();
      setIsGeneratingPDF(false);
      setPdfProgress('');
      showNotification('Geração de PDF cancelada pelo usuário', 'warning');
    }
  };

  // Função para gerar PDF
  const generatePDF = async () => {
    if (isGeneratingPDF) return;
    
    // Validar seleção no modo múltiplo
    if (isMultiSelectMode && selectedOSCs.length === 0) {
      showNotification('Selecione pelo menos uma OSC para gerar o relatório', 'warning');
      return;
    }
    
    // Limpar qualquer AbortController anterior
    if (abortControllerRef.current && !abortControllerRef.current.signal.aborted) {
      abortControllerRef.current.abort();
    }
    
    // Criar novo AbortController
    abortControllerRef.current = new AbortController();
    
    setIsGeneratingPDF(true);
    setPdfProgress('Iniciando geração do PDF...');
    
    try {
      // Preparar filtros baseado no modo de seleção
      let filters;
      if (isMultiSelectMode && selectedOSCs.length > 0) {
        // Modo múltiplo: enviar array de OSCs selecionadas
        filters = {
          selectedOSCs: selectedOSCs, // Array de OSCs
          selectedYear: selectedYear || 'Todos os Anos',
          template: 'institutional',
          multiSelect: true
        };
      } else {
        // Modo único: usar formato tradicional
        filters = OSCReportService.formatFilters(selectedOSC, selectedYear);
      }
      
      setPdfProgress('Gerando PDF...');
      
      // Verificar se não foi cancelado antes de fazer a requisição
      if (abortControllerRef.current.signal.aborted) {
        throw new Error('Operação cancelada');
      }
      
      const result = await OSCReportService.generatePDF(filters, abortControllerRef.current.signal);
      
      // Sucesso
      const oscCount = isMultiSelectMode ? selectedOSCs.length : 1;
      const message = isMultiSelectMode && selectedOSCs.length > 1 
        ? `PDF gerado com sucesso para ${oscCount} OSCs!`
        : 'PDF gerado com sucesso!';
      showNotification(message, 'success');
      
    } catch (error) {
      if (error.name === 'AbortError' || error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
        showNotification('Geração de PDF cancelada', 'warning');
      } else {
        showNotification(`Erro ao gerar PDF: ${error.message}`, 'error');
      }
    } finally {
      setIsGeneratingPDF(false);
      setPdfProgress('');
      abortControllerRef.current = null;
    }
  };

  return (
    <div className="p-6 bg-slate-50 min-h-screen">
      {/* Notificação elegante */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg border-l-4 max-w-md transform transition-all duration-300 ${
          notification.type === 'success' ? 'bg-green-50 border-green-500 text-green-800' :
          notification.type === 'error' ? 'bg-red-50 border-red-500 text-red-800' :
          notification.type === 'warning' ? 'bg-yellow-50 border-yellow-500 text-yellow-800' :
          'bg-blue-50 border-blue-500 text-blue-800'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              {notification.type === 'success' && <CheckCircle className="w-5 h-5" />}
              {notification.type === 'error' && <AlertTriangle className="w-5 h-5" />}
              {notification.type === 'warning' && <AlertTriangle className="w-5 h-5" />}
              {notification.type === 'info' && <FileText className="w-5 h-5" />}
              <span className="font-medium">{notification.message}</span>
            </div>
            <button
              onClick={() => setNotification(null)}
              className="ml-4 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Modal Informativo - Declaração não disponível */}
      {/* Portal: escapa do fluxo normal da página (o div logo abaixo tem
          space-y-6, que deslocaria o overlay fixed se ele ficasse aqui dentro) */}
      {infoModal.isOpen && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full mx-4 transform transition-all">
            {/* Header do Modal */}
            <div className="bg-blue-600 text-white px-6 py-4 rounded-t-lg flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <FileText className="w-6 h-6" />
                <h3 className="text-lg font-semibold">{infoModal.title}</h3>
              </div>
              <button
                onClick={() => setInfoModal({ ...infoModal, isOpen: false })}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Corpo do Modal */}
            <div className="p-6 space-y-4">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-900 mb-1">{infoModal.oscName}</p>
                  <p className="text-gray-600 text-sm leading-relaxed">{infoModal.message}</p>
                </div>
              </div>

              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
                <p className="text-sm text-blue-800">
                  <strong>Informação:</strong> A declaração só pode ser gerada para OSCs que possuem parcerias com status "Em Débito".
                </p>
              </div>
            </div>

            {/* Footer do Modal */}
            <div className="bg-gray-50 px-6 py-4 rounded-b-lg flex justify-end">
              <Button
                onClick={() => setInfoModal({ ...infoModal, isOpen: false })}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Entendi
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header - Título e Descrição */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-slate-900">Relatório por OSC</h1>
          <p className="text-slate-600 mt-2">
            Análise detalhada das parcerias por Organização da Sociedade Civil
          </p>
        </div>

        {/* Controles de Filtros e Exportação */}
        <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm space-y-4">
          {/* Linha 1: Filtros básicos e botão de modo */}
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-end justify-between">
            {/* Filtros */}
            <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
              {/* Seletor de Ano */}
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-slate-700">Ano:</label>
                <Select value={selectedYear} onValueChange={setSelectedYear}>
                  <SelectTrigger className="w-full sm:w-48">
                    <div className="flex-1 truncate text-left pr-2" title={selectedYear}>
                      {selectedYear || 'Selecione um Ano'}
                    </div>
                  </SelectTrigger>
                  <SelectContent className="w-full sm:w-48">
                    <SelectItem value="Todos os Anos">
                      <span className="font-medium">Todos os Anos</span>
                    </SelectItem>
                    {yearList.map(year => (
                      <SelectItem key={year} value={year.toString()}>
                        <span className="font-medium">{year}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Seletor de OSC (modo único) com pesquisa integrada */}
              {!isMultiSelectMode && (
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium text-slate-700">OSC:</label>
                  <div className="w-full sm:w-64 md:w-80 lg:w-96">
                    <OSCCombobox
                      value={selectedOSC}
                      onValueChange={setSelectedOSC}
                      options={['Todas as OSCs', ...oscList]}
                      placeholder="Selecione uma OSC"
                    />
                  </div>
                </div>
              )}

              {/* Botão para alternar modo */}
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium text-slate-700 opacity-0 pointer-events-none">Modo:</label>
                <Button
                  onClick={toggleMultiSelectMode}
                  variant={isMultiSelectMode ? "default" : "outline"}
                  className="whitespace-nowrap"
                >
                  {isMultiSelectMode ? (
                    <>
                      <X className="w-4 h-4 mr-2" />
                      Fechar Seleção Múltipla
                    </>
                  ) : (
                    <>
                      <FilterIcon className="w-4 h-4 mr-2" />
                      Ativar Seleção Múltipla
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Botão de Exportar PDF */}
            <div className="w-full sm:w-auto flex flex-col gap-2">
              <div className="flex gap-2">
                <Button
                  onClick={generatePDF}
                  disabled={isGeneratingPDF || oscStats.length === 0 || (isMultiSelectMode && selectedOSCs.length === 0)}
                  variant="default"
                  className="flex items-center gap-2 whitespace-nowrap bg-red-600 hover:bg-red-700 text-white justify-center flex-1 sm:flex-none"
                >
                  {isGeneratingPDF ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Gerando PDF...
                    </>
                  ) : (
                    <>
                      <FileDown className="w-4 h-4" />
                      Gerar Relatório (PDF)
                    </>
                  )}
                </Button>
                
                {/* Botão de cancelar */}
                {isGeneratingPDF && (
                  <Button
                    onClick={cancelPDFGeneration}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-1 text-red-600 border-red-300 hover:bg-red-50"
                  >
                    <X className="w-4 h-4" />
                    Cancelar
                  </Button>
                )}
              </div>
              
            </div>
          </div>

          {/* Linha 2: Seleção Múltipla (aparece apenas quando ativada) */}
          {isMultiSelectMode && (
            <div className="border-t border-slate-200 pt-4 space-y-3">
              {/* Cabeçalho com indicador visual */}
              <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
                  <span className="text-sm font-semibold text-blue-900">Modo de Seleção Múltipla Ativo</span>
                </div>
                <Button
                  onClick={toggleMultiSelectMode}
                  variant="ghost"
                  size="sm"
                  className="text-blue-700 hover:text-blue-900 hover:bg-blue-100"
                >
                  <X className="w-4 h-4 mr-1" />
                  Fechar
                </Button>
              </div>

              {/* Barra de pesquisa e controles */}
              <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                <div className="relative flex-1 w-full">
                  <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Pesquisar OSCs..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="flex gap-2 w-full sm:w-auto flex-wrap">
                  <Button
                    onClick={selectAllFilteredOSCs}
                    variant="outline"
                    size="sm"
                    className="flex-1 sm:flex-none"
                    disabled={getFilteredOSCList().length === 0}
                  >
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Selecionar Todos
                  </Button>
                  <Button
                    onClick={clearOSCSelection}
                    variant="outline"
                    size="sm"
                    className="flex-1 sm:flex-none"
                    disabled={selectedOSCs.length === 0}
                  >
                    <X className="w-4 h-4 mr-1" />
                    Limpar Seleção
                  </Button>
                  {selectedOSCs.length > 0 && (
                    <Button
                      onClick={() => setShowOnlySelected(!showOnlySelected)}
                      variant={showOnlySelected ? "default" : "outline"}
                      size="sm"
                      className="flex-1 sm:flex-none"
                    >
                      {showOnlySelected ? (
                        <>
                          <FilterIcon className="w-4 h-4 mr-1" />
                          Selecionar Mais OSCs
                        </>
                      ) : (
                        <>
                          <Users className="w-4 h-4 mr-1" />
                          Ver OSCs Selecionadas
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>

              {/* Contador de selecionadas e indicador de filtro */}
              {selectedOSCs.length > 0 && (
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="flex items-center gap-2 text-sm text-blue-600 bg-blue-50 px-3 py-2 rounded-lg flex-1">
                    <CheckCircle className="w-4 h-4" />
                    <span className="font-medium">{selectedOSCs.length} OSC{selectedOSCs.length !== 1 ? 's' : ''} selecionada{selectedOSCs.length !== 1 ? 's' : ''}</span>
                  </div>
                  {showOnlySelected && (
                    <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 px-3 py-2 rounded-lg border border-green-200">
                      <Users className="w-4 h-4" />
                      <span className="font-medium">Exibindo apenas selecionadas</span>
                    </div>
                  )}
                </div>
              )}

              {/* Lista de OSCs com checkboxes */}
              <div className="border border-slate-200 rounded-lg max-h-64 overflow-y-auto">
                {getFilteredOSCList().length === 0 ? (
                  <div className="p-4 text-center text-slate-500 text-sm">
                    {searchTerm ? 'Nenhuma OSC encontrada com este termo' : 'Nenhuma OSC disponível'}
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {getFilteredOSCList().map(osc => (
                      <label
                        key={osc}
                        className="flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={selectedOSCs.includes(osc)}
                          onChange={() => toggleOSCSelection(osc)}
                          className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm text-slate-900 flex-1">{osc}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Cards Informativos */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-600 mb-1">Total de OSCs</p>
                  <p className="text-3xl font-bold text-blue-900">{totalOSCs}</p>
                  <p className="text-xs text-blue-700 mt-1">
                    {selectedYear !== 'Todos os Anos' ? `Ano ${selectedYear}` : 'Todos os anos'}
                  </p>
                </div>
                <div className="p-3 bg-blue-200 rounded-full">
                  <Users className="w-6 h-6 text-blue-700" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-green-50 to-green-100 border-green-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-600 mb-1">Total de Parcerias</p>
                  <p className="text-3xl font-bold text-green-900">{totalPartnerships}</p>
                  <p className="text-xs text-green-700 mt-1">
                    Média: {totalOSCs > 0 ? (totalPartnerships / totalOSCs).toFixed(1) : '0'} por OSC
                  </p>
                </div>
                <div className="p-3 bg-green-200 rounded-full">
                  <BarChart3 className="w-6 h-6 text-green-700" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-purple-600 mb-1">Valor Total</p>
                  <p className="text-2xl font-bold text-purple-900">{formatCurrency(totalValue)}</p>
                  <p className="text-xs text-purple-700 mt-1">
                    Página {currentPage} de {totalPages}
                  </p>
                </div>
                <div className="p-3 bg-purple-200 rounded-full">
                  <DollarSign className="w-6 h-6 text-purple-700" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* OSC Cards */}
        <div className="space-y-6">
          {currentOSCs.map((oscData) => {
            // Regra de pendência aplicada aqui também para o visual do card.
            const hasPendencies = oscData.em_debito > 0 || oscData.verificar > 0;
            
            return (
              <Card key={oscData.name} className="overflow-hidden">
                <CardHeader className={`border-b ${hasPendencies ? 'bg-red-50' : 'bg-green-50'} p-4 sm:p-6`}>
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-base sm:text-xl text-slate-900 flex flex-wrap items-center gap-2 mb-2">
                        <Building2 className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0" />
                        <span className="break-words">{oscData.name}</span>
                        {hasPendencies ? (
                          <Badge className="bg-red-100 text-red-800 border-red-200 text-[10px] sm:text-xs whitespace-nowrap">
                            <AlertTriangle className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-0.5 sm:mr-1" />
                            Com Pendências
                          </Badge>
                        ) : (
                           <Badge className="bg-green-100 text-green-800 border-green-200 text-[10px] sm:text-xs whitespace-nowrap">
                            <CheckCircle className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-0.5 sm:mr-1" />
                            Regular
                          </Badge>
                        )}
                      </CardTitle>
                      <p className="text-xs sm:text-sm text-slate-600 break-all">
                        CNPJ: {oscData.cnpj}
                      </p>
                    </div>
                    <div className="flex flex-col sm:items-end gap-3">
                      <div className="text-left sm:text-right">
                        <p className="text-xl sm:text-2xl font-bold text-slate-800">
                          {formatCurrency(oscData.valor_total)}
                        </p>
                        <p className="text-xs sm:text-sm text-slate-600">
                          {oscData.total} parceria{oscData.total !== 1 ? 's' : ''}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => generateDeclarationPDF(oscData)}
                          size="sm"
                          variant="outline"
                          className="border-blue-300 text-blue-600 hover:bg-blue-50 text-xs sm:text-sm h-8 sm:h-9 whitespace-nowrap"
                          disabled={generatingDeclarationCNPJ === oscData.cnpj}
                        >
                          {generatingDeclarationCNPJ === oscData.cnpj ? (
                            <>
                              <div className="w-3 h-3 mr-1 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                              <span className="hidden sm:inline">Gerando...</span>
                              <span className="sm:hidden">...</span>
                            </>
                          ) : (
                            <>
                              <FileText className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                              <span className="hidden sm:inline">Gerar Declaração</span>
                              <span className="sm:hidden">Declaração</span>
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                
                <CardContent className="p-4 sm:p-6">
                  {/* Status Overview */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 mb-4 sm:mb-6">
                    <div className="text-center">
                      <Badge className="bg-blue-100 text-blue-800 border-blue-200 w-full justify-center text-[10px] sm:text-xs py-1.5 sm:py-2">
                        No Prazo: {oscData.no_prazo}
                      </Badge>
                    </div>
                    <div className="text-center">
                      <Badge className="bg-green-100 text-green-800 border-green-200 w-full justify-center text-[10px] sm:text-xs py-1.5 sm:py-2">
                        Quitado: {oscData.quitado}
                      </Badge>
                    </div>
                    <div className="text-center">
                      <Badge className="bg-red-100 text-red-800 border-red-200 w-full justify-center text-[10px] sm:text-xs py-1.5 sm:py-2">
                        Em Débito: {oscData.em_debito}
                      </Badge>
                    </div>
                    <div className="text-center">
                      <Badge className="bg-amber-100 text-amber-800 border-amber-200 w-full justify-center text-[10px] sm:text-xs py-1.5 sm:py-2">
                        Verificar: {oscData.verificar}
                      </Badge>
                    </div>
                  </div>

                  {/* Partnerships List */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-slate-900 flex items-center gap-2 text-sm sm:text-base">
                      <FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      Parcerias ({oscData.partnerships.length})
                    </h4>
                    <div className="max-h-64 overflow-y-auto space-y-2">
                      {oscData.partnerships.map(partnership => (
                        <div key={partnership.id} className="border border-slate-200 rounded-lg p-2.5 sm:p-3 hover:bg-slate-50">
                          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 mb-2">
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-slate-900 text-sm sm:text-base break-words">
                                {partnership.processo_ano}
                              </p>
                              <p className="text-xs sm:text-sm text-slate-600">
                                Valor: {formatCurrency(partnership.valor || 0)}
                              </p>
                            </div>
                            <Badge className={`${getStatusColor(partnership.status || 'verificar')} border text-[10px] sm:text-xs whitespace-nowrap flex-shrink-0`}>
                              {getStatusLabel(partnership.status || 'verificar')}
                            </Badge>
                          </div>
                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-[10px] sm:text-xs text-slate-500">
                            <span className="flex items-center gap-1 break-all">
                              <Calendar className="w-3 h-3 flex-shrink-0" />
                              {formatDate(partnership.data_inicio)} - {formatDate(partnership.data_fim)}
                            </span>
                            {partnership.ug && (
                              <span className="truncate">UG: {partnership.ug}</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Paginação */}
        {totalPages > 1 && (
          <div className="flex flex-col items-center gap-4 mt-8">
            {/* Controles de Paginação Centralizados */}
            <div className="flex items-center gap-2">
              {/* Botão Anterior */}
              <Button
                variant="outline"
                size="sm"
                onClick={goToPrevious}
                disabled={currentPage === 1}
                className="flex items-center gap-1"
              >
                <ChevronLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Anterior</span>
              </Button>

              {/* Números das páginas */}
              <div className="flex items-center gap-1">
                {/* Primeira página */}
                {currentPage > 3 && (
                  <>
                    <Button
                      variant={1 === currentPage ? "default" : "outline"}
                      size="sm"
                      onClick={() => goToPage(1)}
                      className="w-10 h-10"
                    >
                      1
                    </Button>
                    {currentPage > 4 && (
                      <span className="px-2 text-slate-400">...</span>
                    )}
                  </>
                )}

                {/* Páginas próximas à atual */}
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page => {
                    return page >= currentPage - 2 && page <= currentPage + 2;
                  })
                  .map(page => (
                    <Button
                      key={page}
                      variant={page === currentPage ? "default" : "outline"}
                      size="sm"
                      onClick={() => goToPage(page)}
                      className="w-10 h-10"
                    >
                      {page}
                    </Button>
                  ))}

                {/* Última página */}
                {currentPage < totalPages - 2 && (
                  <>
                    {currentPage < totalPages - 3 && (
                      <span className="px-2 text-slate-400">...</span>
                    )}
                    <Button
                      variant={totalPages === currentPage ? "default" : "outline"}
                      size="sm"
                      onClick={() => goToPage(totalPages)}
                      className="w-10 h-10"
                    >
                      {totalPages}
                    </Button>
                  </>
                )}
              </div>

              {/* Botão Próximo */}
              <Button
                variant="outline"
                size="sm"
                onClick={goToNext}
                disabled={currentPage === totalPages}
                className="flex items-center gap-1"
              >
                <span className="hidden sm:inline">Próximo</span>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            {/* Descrição embaixo da paginação */}
            <div className="text-sm text-slate-600 text-center">
              Mostrando {startIndex + 1} a {Math.min(endIndex, totalOSCs)} de {totalOSCs} OSCs
            </div>
          </div>
        )}

        {oscStats.length === 0 && !isLoading && (
          <Card className="p-12 text-center">
            <Building2 className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              Nenhuma OSC encontrada
            </h3>
            <p className="text-slate-500">
              Não há parcerias cadastradas para gerar o relatório.
            </p>
          </Card>
        )}

        {/* Botão Voltar ao Topo - Aparece apenas quando há conteúdo */}
        {oscStats.length > 0 && (
          <div className="flex justify-end mt-8">
            <Button
              onClick={scrollToTop}
              variant="outline"
              size="lg"
              className="flex items-center gap-2 animate-pulse hover:animate-none transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl bg-white/80 backdrop-blur-sm border-2 border-blue-200 hover:border-blue-400 text-blue-700 hover:text-blue-800"
            >
              <ArrowUp className="w-5 h-5" />
              Voltar ao Topo
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
